import type { TabsProps } from "antd";
import { Button, Col, Flex, Input, Row, Tabs } from "antd";
import to from "await-to-js";
import { t } from "i18next";
import { useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { InfoCircleFilled } from "@ant-design/icons";
import { Typography } from "antd";
import FileUploader from "../../../../components/FileUploader/FileUploader";
import { GoBackButton } from "../../../../components/GoBackButton/GoBackButton";
import { MandatoryField } from "../../../../components/MandatoryField/MandatoryField";
import { ModalsContext } from "../../../../context/modals.context";
import { ExciseStampsContext } from "../../../../modules/obligee/exciseStamps/exciseStamps.context";
import { exciseStampsService } from "../../../../modules/obligee/exciseStamps/exciseStamps.service";
import {
  AkciznaMarkicaKategorijaProzivoda,
  TipObveznika,
} from "../../../../modules/obligee/exciseStamps/exciseStamps.types";
import { DocumentApi } from "../../../../types";
import { ForeignProducerInfo } from "../components/ForeignProducerInfo/ForeignProducerInfo";
import { RequestedStampsInfo } from "../components/RequestedStampsInfo/RequestedStampsInfo";
import { UserInfo } from "../components/UserInfo/UserInfo";
import { ProductBrandsData } from "./components/ProductBrandsData/ProductBrandsData";
import { Retail } from "./components/Retail/Retail";
import style from "./ExciseStampNewRequestForm.module.scss";

const { Text } = Typography;
interface IExciseStampNewRequestFormProps {
  title: string;
  goBack?: () => void;
}

const ExciseStampNewRequest = ({ title }: IExciseStampNewRequestFormProps) => {
  const { exciseStamps, setExciseStamps } = useContext(ExciseStampsContext);
  const { setError } = useContext(ModalsContext);

  const navigate = useNavigate();

  const isUvoznik = exciseStamps.tipObveznikaIds.includes(2);
  const isProizvodjac = exciseStamps.tipObveznikaIds.includes(1);
  const isPP = exciseStamps.tipObveznikaIds.includes(
    TipObveznika.POSEBNI_POSTUPAK
  );

  const toggleForeign = () => {
    if (isUvoznik || exciseStamps.isPP)
      return (
        <ForeignProducerInfo
          isUvoznik={isUvoznik}
          isPP={isPP && exciseStamps.isPP}
        />
      );
    if (isProizvodjac && exciseStamps.isPP)
      return (
        <ForeignProducerInfo
          isUvoznik={isUvoznik}
          isPP={isPP && exciseStamps.isPP}
        />
      );
    return null;
  };

  const postKreiranjeZahtevaZaMarkicu = async () => {
    const handlePayload = () => {
      return {
        brojPorudzbenice: exciseStamps.brojPorudzbenice,
        markicaKategorijaId: exciseStamps.kategorijaProizvodaId,
        tipObveznikaIds: exciseStamps.tipObveznikaIds,
        adresaSlanjaMarkica: exciseStamps.adresaSlanjaMarkica,
        podaciOTrazenimMarkicama: exciseStamps.podaciOTrazenimMarkicama,
        inostraniProizvodjac:
          (!exciseStamps.isProizvodjacRequired && (isUvoznik || isPP)) ||
          (!isUvoznik && !isPP)
            ? undefined
            : exciseStamps.inostraniProizvodjac,
        distributer:
          (!exciseStamps.isDistributerRequired && (isUvoznik || isPP)) ||
          (!isUvoznik && !isPP)
            ? undefined
            : exciseStamps.distributer,
        MarkicaZahtevID: exciseStamps.zahtevMarkicaId,
        dokumentSpecifikacija: exciseStamps.dokumentSpecifikacija,
      };
    };

    const [err, res] = await to(
      exciseStampsService.postKreiranjeZahtevaZaMarkicu(handlePayload())
    );
    if (err || !res.success) return setError(res?.errorMessage);

    setExciseStamps({
      zahtevMarkicaId: res.data?.id,
      evidencioniBroj: res.data?.brojZahteva,
    });

    navigate("/obveznik/akcizneMarkice/zahtevPodnet");
  };

  const handleSendRequest = () => {
    postKreiranjeZahtevaZaMarkicu();
  };

  const handleUpload = (document: DocumentApi) => {
    setExciseStamps({ dokumentSpecifikacija: document });
  };

  const handleRemove = () => {
    setExciseStamps({ dokumentSpecifikacija: null });
  };

  const isUploadSpecificationVisible = useMemo(() => {
    return (
      exciseStamps.kategorijaProizvodaId ===
        AkciznaMarkicaKategorijaProzivoda.KAFA ||
      exciseStamps.kategorijaProizvodaId ===
        AkciznaMarkicaKategorijaProzivoda.ALKOHOL
    );
  }, [exciseStamps.kategorijaProizvodaId]);

  const isSubmitDisabled = useMemo(() => {
    const isDistributerNotValid =
      !exciseStamps.distributer.adresa ||
      !exciseStamps.distributer.mesto ||
      !exciseStamps.distributer.naziv ||
      !exciseStamps.distributer.poreskiBroj;

    const isProizvodjacNotValid =
      !exciseStamps.inostraniProizvodjac.adresa ||
      !exciseStamps.inostraniProizvodjac.mesto ||
      !exciseStamps.inostraniProizvodjac.naziv ||
      !exciseStamps.inostraniProizvodjac.poreskiBroj;

    if (
      exciseStamps.isDistributerRequired &&
      isDistributerNotValid &&
      (isUvoznik || isPP)
    ) {
      return true;
    }

    if (
      exciseStamps.isProizvodjacRequired &&
      isProizvodjacNotValid &&
      (isUvoznik || isPP)
    ) {
      return true;
    }

    if (!exciseStamps.dokumentSpecifikacija && isUploadSpecificationVisible) {
      return true;
    }

    return !exciseStamps.podaciOTrazenimMarkicama?.length;
  }, [
    isPP,
    isUvoznik,
    exciseStamps.distributer,
    exciseStamps.inostraniProizvodjac,
    exciseStamps.isDistributerRequired,
    exciseStamps.isProizvodjacRequired,
    exciseStamps.podaciOTrazenimMarkicama,
    exciseStamps.dokumentSpecifikacija,
    isUploadSpecificationVisible,
  ]);

  return (
    <>
      <Col className={style.mainTitle} span={24}>
        <h2>{t("AKCIZNE_MARKICE.ZAHTEV_ZA_AKCIZNU_MARKICU") + ` ` + title}</h2>
      </Col>
      <UserInfo
        exciseStampsInfo={exciseStamps.drustvoInformationMarkiceZahtev}
      />
      {toggleForeign()}
      <Col span={24}>
        <div className={style.title}>
          {t("AKCIZNE_MARKICE.PODACI_O_PORUDZBENICI")}
        </div>
        <Row className={style.row}>
          <Col className="yCenter" span={5}>
            <span className={style.fontSize}>
              {t("AKCIZNE_MARKICE.BROJ_PORUDZBENICE")}:
            </span>
          </Col>
          <Col span={5}>
            <Input
              name="broj-porudzbenice-markice"
              className={style.fontSize}
              onChange={(e) =>
                setExciseStamps({ brojPorudzbenice: e.target.value })
              }
            />
          </Col>
          <Col span={7}></Col>
          <Col span={5}></Col>
        </Row>
      </Col>
      {isUploadSpecificationVisible ? (
        <Col span={24}>
          <div className={style.title}>
            {t(
              "AKCIZNE_MARKICE.INFORMACIJE_O_SPECIFIKACIJI_KOLICINE_TRAZENIH_MARKICA"
            )}
          </div>
          <Row className={style.row}>
            <Col className="yCenter" span={5}>
              <span className={style.fontSize}>
                {MandatoryField("AKCIZNE_MARKICE.DOKUMENT")}
              </span>
            </Col>
            <FileUploader onUpload={handleUpload} onRemove={handleRemove} />
          </Row>

          <Flex align="center" gap={8} className={style.uploadInfo}>
            <InfoCircleFilled className={style.infoIcon} />
            <Text className={`${style.uploadInfoText} ${style.fontSize}`}>
              {exciseStamps?.kategorijaProizvodaId ===
              AkciznaMarkicaKategorijaProzivoda.KAFA
                ? t("AKCIZNE_MARKICE.UPLOAD_SPECIFIKACIJE_KAFA_INFO")
                : t("AKCIZNE_MARKICE.UPLOAD_SPECIFIKACIJE_ALKOHOL_INFO")}
            </Text>
          </Flex>
        </Col>
      ) : (
        <></>
      )}
      <RequestedStampsInfo
        title={title}
        requestedStampsInfo={exciseStamps.podaciOTrazenimMarkicamaPrint}
      />
      <Col className={style.buttonCol} span={24}>
        <Button
          type="primary"
          disabled={isSubmitDisabled}
          onClick={handleSendRequest}
        >
          {t("AKCIZNE_MARKICE.POSALJI_ZAHTEV")}
        </Button>
      </Col>
    </>
  );
};
export const ExciseStampNewRequestForm: React.FC<
  IExciseStampNewRequestFormProps
> = ({ title, goBack }) => {
  const { exciseStamps } = useContext(ExciseStampsContext);

  const tabs: TabsProps["items"] = [
    {
      key: "1",
      label: t("AKCIZNE_MARKICE.ZAHTEV_ZA_AKCIZNE_MARKICE"),
      children: <ExciseStampNewRequest title={title} />,
    },
    {
      key: "2",
      label: t("AKCIZNE_MARKICE.MALOPRODAJNE_CENE"),
      children: <Retail />,
    },
    {
      key: "3",
      label: t("AKCIZNE_MARKICE.PODACI_O_MARKAMA_PROIZVODA"),
      children: <ProductBrandsData />,
    },
  ];

  const handleRetailPrice = () => {
    if (exciseStamps.prikaziMaloprodajneCene)
      return <Tabs defaultActiveKey="1" items={tabs} />;
    return <ExciseStampNewRequest title={title} />;
  };

  return (
    <Row className={style.mainContainer}>
      <GoBackButton beforeNavigate={goBack} />
      <div className={style.contentWrapper}>{handleRetailPrice()}</div>
    </Row>
  );
};
